import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Download,
  Loader2,
  User,
  Users,
  UserCheck,
  Calendar,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Eye,
  X,
  Upload,
  File as FileIcon,
} from "lucide-react";
import api from "../services/api";
import toast from "react-hot-toast";
import HelpButton from "../components/HelpButton";
import { uploadFilesViaS3 } from "../utils/uploadToS3";

function ApplicationDocuments() {
  const navigate = useNavigate();
  const { applicationId } = useParams();
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [counts, setCounts] = useState({});
  const [selectedType, setSelectedType] = useState("user");
  const [expandedProfiles, setExpandedProfiles] = useState({});
  const [expandedDocuments, setExpandedDocuments] = useState({});
  const [viewingFile, setViewingFile] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadRemarks, setUploadRemarks] = useState("");
  const [uploading, setUploading] = useState(false);

  const typeOptions = [
    { value: "user", label: "User Documents", icon: User },
    { value: "system", label: "System Documents", icon: FileText },
    { value: "deferral", label: "Deferral Documents", icon: Calendar },
    { value: "pdd", label: "PDD Documents", icon: Users },
  ];

  useEffect(() => {
    loadDocuments();
  }, [selectedType]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get("/api/mobile/applications/documents", {
        params: {
          application_id: applicationId,
          type: selectedType,
        },
      });

      if (response.data.success) {
        setProfiles(response.data.data.profiles || []);
        setCounts(response.data.data.counts || {});
        
        // Auto-expand first profile
        if (response.data.data.profiles?.length > 0) {
          setExpandedProfiles({ [response.data.data.profiles[0].profile_id]: true });
        }
      }
    } catch (error) {
      console.error("Failed to load documents:", error);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const toggleProfile = (profileId) => {
    setExpandedProfiles((prev) => ({
      ...prev,
      [profileId]: !prev[profileId],
    }));
  };

  const toggleDocument = (docId) => {
    setExpandedDocuments((prev) => ({
      ...prev,
      [docId]: !prev[docId],
    }));
  };

  const getProfileIcon = (profileType) => {
    switch (profileType?.toUpperCase()) {
      case "APPLICANT":
        return <User className="w-5 h-5" />;
      case "CO-APPLICANT":
        return <Users className="w-5 h-5" />;
      case "GUARANTOR":
        return <UserCheck className="w-5 h-5" />;
      default:
        return <User className="w-5 h-5" />;
    }
  };

  const getProfileColor = (profileType) => {
    switch (profileType?.toUpperCase()) {
      case "APPLICANT":
        return "from-blue-500 to-cyan-500";
      case "CO-APPLICANT":
        return "from-purple-500 to-pink-500";
      case "GUARANTOR":
        return "from-green-500 to-emerald-500";
      default:
        return "from-gray-500 to-slate-500";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const getFileUrl = async (file) => {
    const response = await api.post("/api/mobile/applications/documents/view-url", {
      application_id: applicationId,
      file_id: file.id,
      file_path: file.file_path,
    });

    if (!response?.data?.success || !response.data?.data?.url) {
      throw new Error(response?.data?.message || "Failed to get file URL");
    }

    return response.data.data.url;
  };

  const isViewableFile = (fileName) => {
    if (!fileName) return false;
    const extension = fileName.toLowerCase().split('.').pop();
    const viewableExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf'];
    return viewableExtensions.includes(extension);
  };

  const isImageFile = (fileName) => {
    if (!fileName) return false;
    const extension = fileName.toLowerCase().split('.').pop();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    return imageExtensions.includes(extension);
  };

  const isPdfFile = (fileName) => {
    if (!fileName) return false;
    return fileName.toLowerCase().endsWith('.pdf');
  };

  const viewFile = async (file) => {
    const toastId = toast.loading("Preparing file…");
    try {
      const url = await getFileUrl(file);
      toast.dismiss(toastId);
      setViewingFile({
        url,
        file_path: file.file_path,
        file_id: file.id,
        name: decodeURIComponent(file.file_name),
        isImage: isImageFile(file.file_name),
        isPdf: isPdfFile(file.file_name),
      });
    } catch (error) {
      toast.dismiss(toastId);
      console.error("Failed to load file:", error);
      toast.error(error?.response?.data?.message || "Failed to load file");
    }
  };

  const downloadFile = async (file) => {
    const toastId = toast.loading("Preparing download…");
    try {
      const fileUrl = await getFileUrl(file);
      toast.dismiss(toastId);

      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", decodeURIComponent(file.file_name));
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("File download started");
    } catch (error) {
      toast.dismiss(toastId);
      console.error("Failed to download file:", error);
      toast.error(error?.response?.data?.message || "Failed to download file");
    }
  };

  const openUploadModal = (document) => {
    setSelectedDocument(document);
    setUploadFiles([]);
    setUploadRemarks("");
    setUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    setUploadModalOpen(false);
    setSelectedDocument(null);
    setUploadFiles([]);
    setUploadRemarks("");
  };

  const handleFileSelect = async (e) => {
    const picked = Array.from(e.target.files);
    // Materialize bytes immediately. Android content:// URIs can become invalid
    // by the time we read them later, causing silent upload truncation.
    try {
      const materialized = await Promise.all(
        picked.map(async (f) => {
          const buf = await f.arrayBuffer();
          return new File([buf], f.name, {
            type: f.type || "application/octet-stream",
            lastModified: f.lastModified,
          });
        })
      );
      setUploadFiles(materialized);
    } catch (err) {
      console.error("Failed to read selected files", err);
      toast.error("Could not read one of the selected files. Please re-pick.");
      setUploadFiles([]);
    }
  };

  const handleUpload = async () => {
    if (!selectedDocument || uploadFiles.length === 0) {
      toast.error("Please select at least one file");
      return;
    }

    setUploading(true);
    let step = "presign+s3_put";
    try {
      const upload = await uploadFilesViaS3(
        "/api/mobile/upload-pdd-deferral-document/presign",
        uploadFiles,
        { document_id: selectedDocument.id }
      );

      step = "commit";
      const response = await api.post(
        "/api/mobile/upload-pdd-deferral-document/commit",
        {
          upload_id: upload.upload_id,
          document_id: selectedDocument.id,
          remarks: uploadRemarks || null,
          files: upload.files,
        },
        { timeout: 60000 }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Document uploaded successfully");
        closeUploadModal();
        loadDocuments();
      }
    } catch (error) {
      console.error(`Failed to upload document (step=${step}):`, error);
      console.error("Error response:", error.response);
      console.error("Error request:", error.request);
      console.error("Error message:", error.message);
      console.error("Full error object:", JSON.stringify(error, Object.getOwnPropertyNames(error), 2));

      // Handle different error response structures
      let errorMessage = "Failed to upload document";

      if (error.response?.data) {
        errorMessage = error.response.data.message || errorMessage;

        if (error.response.data.errors) {
          const validationErrors = Object.values(error.response.data.errors).flat();
          errorMessage += ": " + validationErrors.join(", ");
        }
      } else if (error.request) {
        errorMessage = error.message || "No response from server";
      } else {
        errorMessage = error.message || errorMessage;
      }

      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const getDocTypeColor = (docBy) => {
    switch (docBy?.toUpperCase()) {
      case "USER":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300";
      case "SYSTEM":
        return "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
    }
  };

  const getPddStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "PENDING":
        return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300";
      case "APPROVED":
        return "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300";
      case "REJECTED":
        return "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 transition-colors duration-300">
      {/* Header */}
      <div className="sticky top-0 z-20 backdrop-blur-xl bg-white/80 dark:bg-gray-900/80 border-b border-white/20 dark:border-gray-800 shadow-lg safe-top transition-colors duration-300">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors active:scale-95"
            >
              <ArrowLeft className="w-6 h-6 text-gray-700 dark:text-gray-300" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Application Documents
              </h1>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                App ID: #{applicationId}
              </p>
            </div>
          </div>

          {/* Type Selector */}
          <div className="grid grid-cols-2 gap-2">
            {typeOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = selectedType === option.value;
              const count = counts[option.value] || 0;
              
              return (
                <button
                  key={option.value}
                  onClick={() => setSelectedType(option.value)}
                  className={`flex items-center justify-between gap-2 px-4 py-3 rounded-2xl font-semibold transition-all ${
                    isSelected
                      ? "bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg"
                      : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    <span className="text-sm">{option.label}</span>
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      isSelected
                        ? "bg-white/20 text-white"
                        : "bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 pb-32">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900 dark:to-purple-900 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <FileText className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              No Documents Found
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              There are no {selectedType} documents available.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {profiles.map((profile) => {
              const isProfileExpanded = expandedProfiles[profile.profile_id];
              
              return (
                <div
                  key={profile.profile_id}
                  className="bg-white dark:bg-gray-800 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden"
                >
                  {/* Profile Header */}
                  <button
                    onClick={() => toggleProfile(profile.profile_id)}
                    className="w-full px-5 py-4 flex items-center justify-between gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-12 h-12 bg-gradient-to-br ${getProfileColor(
                          profile.profile_type
                        )} rounded-2xl flex items-center justify-center shadow-md text-white`}
                      >
                        {getProfileIcon(profile.profile_type)}
                      </div>
                      <div className="text-left">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                          {profile.profile_name}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {profile.profile_type} • {profile.documents?.length || 0} Documents
                        </p>
                      </div>
                    </div>
                    {isProfileExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </button>

                  {/* Documents List */}
                  {isProfileExpanded && profile.documents && (
                    <div className="px-5 pb-4 space-y-3">
                      {profile.documents.map((document) => {
                        const isDocExpanded = expandedDocuments[document.id];
                        const hasFiles = document.files && document.files.length > 0;

                        return (
                          <div
                            key={document.id}
                            className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-700/50 dark:to-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-600"
                          >
                            {/* Document Header */}
                            <div className="p-4">
                              <div className="flex items-start justify-between gap-3 mb-3">
                                <div className="flex-1 min-w-0">
                                  <div className="mb-2">
                                    <h4 className="text-sm font-bold text-gray-900 dark:text-white break-all mb-2">
                                      {document.doc_type}
                                    </h4>
                                    {(selectedType === 'pdd' || selectedType === 'deferral') && document.doc_pdd_status && (
                                      <span
                                        className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${getPddStatusColor(
                                          document.doc_pdd_status
                                        )}`}
                                      >
                                        {document.doc_pdd_status}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-600 dark:text-gray-400 break-all">
                                    {document.doc_category}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {(selectedType === 'pdd' || selectedType === 'deferral') && (
                                    <button
                                      onClick={() => openUploadModal(document)}
                                      className="px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl text-xs font-semibold hover:shadow-lg transition-all active:scale-95 flex items-center gap-1"
                                      title="Upload files"
                                    >
                                      <Upload className="w-3 h-3" />
                                      Upload
                                    </button>
                                  )}
                                  {hasFiles && (
                                    <button
                                      onClick={() => toggleDocument(document.id)}
                                      className="px-3 py-1.5 bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 rounded-xl text-xs font-semibold hover:bg-indigo-200 dark:hover:bg-indigo-800 transition-colors flex items-center gap-1"
                                    >
                                      <FileIcon className="w-3 h-3" />
                                      {document.files.length} File{document.files.length > 1 ? "s" : ""}
                                      {isDocExpanded ? (
                                        <ChevronUp className="w-3 h-3" />
                                      ) : (
                                        <ChevronDown className="w-3 h-3" />
                                      )}
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* Document Details */}
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                {document.latest_version && (
                                  <div>
                                    <p className="text-gray-500 dark:text-gray-400">Version</p>
                                    <p className="font-semibold text-gray-900 dark:text-white">
                                      {document.latest_version}
                                    </p>
                                  </div>
                                )}
                                {document.uploaded_on && (
                                  <div>
                                    <p className="text-gray-500 dark:text-gray-400">Generated</p>
                                    <p className="font-semibold text-gray-900 dark:text-white">
                                      {formatDate(document.uploaded_on)}
                                    </p>
                                  </div>
                                )}
                                {document.uploaded_by && (
                                  <div>
                                    <p className="text-gray-500 dark:text-gray-400">Uploaded By</p>
                                    <p className="font-semibold text-gray-900 dark:text-white">
                                      {document.uploaded_by.name}
                                    </p>
                                  </div>
                                )}
                                {document.doc_expires_at && (
                                  <div>
                                    <p className="text-gray-500 dark:text-gray-400">Expires</p>
                                    <p className="font-semibold text-gray-900 dark:text-white">
                                      {formatDate(document.doc_expires_at)}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Files List */}
                            {isDocExpanded && hasFiles && (
                              <div className="px-4 pb-4 space-y-2">
                                {document.files.map((file) => (
                                  <div
                                    key={file.id}
                                    className="bg-white dark:bg-gray-800 rounded-xl p-3 flex items-center justify-between gap-3"
                                  >
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                        {decodeURIComponent(file.file_name)}
                                      </p>
                                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        <span>v{file.version}</span>
                                        <span>•</span>
                                        <span>{formatFileSize(file.file_size)}</span>
                                        <span>•</span>
                                        <span>{formatDate(file.uploaded_at)}</span>
                                      </div>
                                      {file.file_remarks && (
                                        <div className="mt-2 px-2 py-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                          <p className="text-xs text-gray-700 dark:text-gray-300">
                                            <span className="font-semibold text-blue-700 dark:text-blue-400">Remarks: </span>
                                            {file.file_remarks}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      {isViewableFile(file.file_name) && (
                                        <button
                                          onClick={() => viewFile(file)}
                                          className="p-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:shadow-lg transition-all active:scale-95"
                                          title="View file"
                                        >
                                          <Eye className="w-4 h-4" />
                                        </button>
                                      )}
                                      <button
                                        onClick={() => downloadFile(file)}
                                        className="p-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:shadow-lg transition-all active:scale-95"
                                        title="Download file"
                                      >
                                        <Download className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {!hasFiles && (
                              <div className="px-4 pb-4">
                                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 text-center">
                                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                                    No files uploaded yet
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* File Viewer Modal */}
      {viewingFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
          <div className="relative w-full h-full max-w-6xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold text-lg truncate flex-1 mr-4">
                {viewingFile.name}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => downloadFile({ id: viewingFile.file_id, file_path: viewingFile.file_path, file_name: viewingFile.name })}
                  className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors"
                  title="Download"
                >
                  <Download className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewingFile(null)}
                  className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 bg-white rounded-2xl overflow-hidden flex items-center justify-center">
              {viewingFile.isImage ? (
                <img
                  src={viewingFile.url}
                  alt={viewingFile.name}
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => {
                    toast.error("Failed to load image");
                    setViewingFile(null);
                  }}
                />
              ) : viewingFile.isPdf ? (
                <iframe
                  src={viewingFile.url}
                  className="w-full h-full"
                  title={viewingFile.name}
                  onError={(e) => {
                    toast.error("Failed to load PDF");
                    setViewingFile(null);
                  }}
                />
              ) : (
                <div className="text-center p-8">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">Cannot preview this file type</p>
                  <button
                    onClick={() => downloadFile({ id: viewingFile.file_id, file_path: viewingFile.file_path, file_name: viewingFile.name })}
                    className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:shadow-lg transition-all"
                  >
                    Download File
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {uploadModalOpen && selectedDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
          <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-3xl shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                  Upload Document Files
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 break-all">
                  {selectedDocument.doc_type}
                </p>
              </div>
              <button
                onClick={closeUploadModal}
                disabled={uploading}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* File Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Select Files *
                </label>
                <div className="relative">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    disabled={uploading}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl text-sm text-gray-900 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 dark:file:bg-indigo-900 dark:file:text-indigo-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                {uploadFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                      Selected Files ({uploadFiles.length}):
                    </p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {uploadFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl"
                        >
                          <span className="text-xs font-medium text-gray-900 dark:text-white truncate flex-1">
                            {file.name}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            {(file.size / 1024).toFixed(1)} KB
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Remarks (Optional)
                </label>
                <textarea
                  value={uploadRemarks}
                  onChange={(e) => setUploadRemarks(e.target.value)}
                  disabled={uploading}
                  rows="3"
                  placeholder="Add any notes or comments about these files..."
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-2xl text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none disabled:opacity-50"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={closeUploadModal}
                disabled={uploading}
                className="px-6 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-2xl text-sm font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || uploadFiles.length === 0}
                className="px-6 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-2xl text-sm font-semibold hover:shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload Files
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Help Button */}
      <HelpButton />
    </div>
  );
}

export default ApplicationDocuments;
